/**
 * Navigation components.
 * @module components/theme/Navigation/Navigation
 */

import { useEffect, useMemo } from 'react';
import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';
import { getGovBRMenu } from 'govbr.frontend.policy/actions/govbr_menu/govbr_menu';
import { getNavigationWithQs } from 'govbr.frontend.policy/actions/navigation_qs/navigation_qs';
import { SiteNavigation, SocialLinks } from 'govbr.frontend.policy/components';
// import { NavigationFooterLinks } from '../../../../components';
import menuMessages from 'govbr.frontend.policy/messages/navigation';
import socialMessages from 'govbr.frontend.policy/messages/social';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';

function getLanguageRoot(pathname, locale) {
  const formatedLocale = locale?.toLowerCase();

  // fallback seguro
  return `/${formatedLocale}`;
}

const Navigation = ({
  active,
  pathname,
  onClose,
  token,
  items,
  intl,
  lang,
}) => {
  const { settings } = config;
  const dispatch = useDispatch();

  const { items: govBRItems, loaded: govBRLoaded } = useSelector(
    (state) => state.govbr_menu,
  );

  const { needsNavigationRefresh } = useSelector(
    (state) => state.refresh_navigation,
  );

  const navigationRoot = useMemo(
    () => getLanguageRoot(pathname, lang),
    [pathname, lang],
  );

  useEffect(() => {
    if (!navigationRoot || !needsNavigationRefresh) return;

    if (!hasApiExpander('navigation', navigationRoot)) {
      dispatch(
        getNavigationWithQs(
          navigationRoot,
          settings.navDepth,
          !!token ? '&l=1' : '',
        ),
      );
      dispatch({ type: 'CLEAR_NAVIGATION_REFRESH' });
    }
  }, [
    token,
    navigationRoot,
    dispatch,
    settings.navDepth,
    needsNavigationRefresh,
  ]);

  useEffect(() => {
    if (!govBRLoaded) {
      dispatch(getGovBRMenu());
    }
  }, [dispatch, govBRLoaded]);

  const site_properties = useSelector((state) => state.site_properties.data);

  return (
    <SiteNavigation
      onToggleMenu={() => onClose && onClose()}
      active={active}
      menu-label={intl.formatMessage(menuMessages.menu_label)}
      close-label={intl.formatMessage(menuMessages.close_label)}
    >
      <SiteNavigation.Group
        items={items}
        title={site_properties.site_title}
        lang={lang}
      />

      <SiteNavigation.Group items={govBRItems} logo />

      <SiteNavigation.Footer>
        <div className="br-menu">
          <div className="menu-footer">
            <div className="menu-links px-4">
              <a href="https://www.gov.br/pt-br/apps/@@galeria-de-aplicativos">
                <span className="mr-1">Galeria de aplicativos</span>
                <i
                  className="fas fa-external-link-square-alt"
                  aria-hidden="true"
                />
              </a>
              <a href="https://www.gov.br/pt-br/participacao-social">
                <span className="mr-1">Participe</span>
                <i
                  className="fas fa-external-link-square-alt"
                  aria-hidden="true"
                />
              </a>
            </div>

            <div className="sharegroup px-4 py-2">
              <SocialLinks
                labelClassName="menu-social text-semi-bold mb-1"
                label={intl.formatMessage(socialMessages.social_networks)}
              />
            </div>

            <div className="menu-info">
              <div className="text-center text-down-01">
                Todo o conteúdo deste site está publicado sob a licença{' '}
                <strong>Creative Commons Atribuição-SemDerivações 3.0</strong>
              </div>
            </div>
          </div>
        </div>
      </SiteNavigation.Footer>
    </SiteNavigation>
  );
};

Navigation.propTypes = {
  active: PropTypes.bool,
  getNavigation: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  ).isRequired,
  lang: PropTypes.string.isRequired,
};

Navigation.defaultProps = { active: false };

export default compose(
  injectIntl,
  connect(
    (state) => ({
      token: state.userSession.token,
      items: state.navigation.items,
      lang: state.intl.locale,
    }),
    { getNavigation },
  ),
)(Navigation);
